import axios from 'axios';
import api from './api';

// Auth lives on Platform.Auth (:5001), not Webmail.API.
// All token issuance/refresh/revoke goes through Platform.Auth.
// Webmail.API (:5002) only exposes /api/auth/me (a thin profile endpoint that
// reads claims and resolves to the local Webmail user record).
const AUTH_BASE = process.env.REACT_APP_AUTH_URL || 'http://localhost:5001';
const AUTH_API  = `${AUTH_BASE}/api/v1/auth`;

export const authService = {
  // POST /auth/login on Platform.Auth.
  // Same identifier-based contract as the CRM client. Multi-tenant Webmail
  // users surface their tenant via the user.tenantID / user.externalId fields
  // returned in the response (and the corresponding tid/extId JWT claims).
  async login(identifier, password) {
    const res = await axios.post(`${AUTH_API}/login`, { identifier, password });
    const { accessToken, refreshToken, user } = res.data;
    const token = accessToken || res.data.token;

    if (token)        localStorage.setItem('webmail_token', token);
    if (refreshToken) localStorage.setItem('webmail_refresh_token', refreshToken);
    if (user)         localStorage.setItem('webmail_user', JSON.stringify(user));

    return { ...res.data, token };
  },

  // Email-based login retained for backward compat (just delegates to login).
  async loginWithEmail(email, password) {
    return this.login(email, password);
  },

  // CRM SSO bridge: the CRM passes the existing access token on to Webmail.
  // No separate /auth/crm-login any more — same Platform.Auth token is reused.
  async loginFromCrm(token) {
    if (token) {
      localStorage.setItem('webmail_token', token);
    }
    return this.validateToken(token);
  },

  // Resolve current user via Webmail.API/auth/me — gives mailbox/tenant context
  // that lives in the Webmail database. The token MUST have been issued by
  // Platform.Auth (Webmail.API only validates, never issues).
  async validateToken(token) {
    const t = token || localStorage.getItem('webmail_token');
    const res = await api.get('/auth/me', {
      headers: { Authorization: `Bearer ${t}` }
    });
    return res.data;
  },

  // POST /auth/refresh on Platform.Auth — rotates the refresh token.
  async refreshToken() {
    const refreshToken = localStorage.getItem('webmail_refresh_token');
    if (!refreshToken) return null;

    try {
      const res = await axios.post(`${AUTH_API}/refresh`, { refreshToken });
      const { accessToken, refreshToken: newRefresh } = res.data;
      if (accessToken) localStorage.setItem('webmail_token', accessToken);
      if (newRefresh)  localStorage.setItem('webmail_refresh_token', newRefresh);
      return accessToken;
    } catch (err) {
      console.warn('[authService] refresh failed:', err.message);
      this.logout();
      return null;
    }
  },

  // Revoke server-side, then clear local state.
  async logout() {
    const refreshToken = localStorage.getItem('webmail_refresh_token');
    if (refreshToken) {
      try {
        await axios.post(`${AUTH_API}/logout`, { refreshToken });
      } catch (err) {
        console.warn('[authService] logout request failed:', err.message);
      }
    }
    localStorage.removeItem('webmail_token');
    localStorage.removeItem('webmail_refresh_token');
    localStorage.removeItem('webmail_user');
  }
};
